import { useCallback, useEffect, useRef, useState } from 'react';
import { Message, Space, Tooltip } from '@arco-design/web-react';
import { useAuthStore } from '../../store/auth';
import { useAgentStore } from '../../store/agent';
import { usePhoneStore } from '../../store/phone';
import {
  CHANNEL_MODES_EVENT,
  WORKBENCH_SURFACE_EVENT,
  getMyToday,
  modeToSwitches,
  setMyChannelMode,
  setCachedEffectiveMode,
  type WorkbenchSurface,
} from '../../api/shift';

/**
 * 顶栏「在线会话 / 电话」**单一互斥**渠道开关。
 *
 * 口径（2026-10）：
 * - 两个渠道**彻底分开**：任一时刻只接一种——会话（chat_only）或电话（voice_only），
 *   不再有「双开（mixed）」；「在线可以同时接多个会话」由会话容量（chat_max）与在线界面多标签承担；
 * - 入口统一：工作台只有一个菜单/一个页面，本开关**不跳路由**，而是广播 `WORKBENCH_SURFACE_EVENT`
 *   让工作台在「电话界面 / 在线界面」之间切换；后端推送（来电/派会话）也会自动切到对应界面；
 * - 通话中锁定电话：会话侧置灰并提示「挂机后再切换」，杜绝「一边通话一边接在线」；
 * - 互斥失败（接口报错）时开关回到原渠道，不显示成功。
 */
type Channel = 'chat' | 'voice';

const surfaceOf = (channel: Channel): WorkbenchSurface => (channel === 'chat' ? 'online' : 'phone');

export default function ChannelToggles() {
  const user = useAuthStore((s) => s.user);
  const agent = useAgentStore();
  /** 通话中（软电话状态机 in-call）：会话不可选，避免一边通话一边接在线会话 */
  const inCall = usePhoneStore((s) => s.status === 'in-call');

  // 初值：按工作台记住的界面落位（避免刷新后界面与开关不一致；键由 pages/workbench/index.tsx 维护）
  const [channel, setChannel] = useState<Channel>(() => {
    try {
      return sessionStorage.getItem('bcc-wb-surface') === 'online' ? 'chat' : 'voice';
    } catch {
      return 'voice';
    }
  });
  /** 当日生效渠道是否已拉到（用于初值落位与界面同步） */
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 最新渠道（异步回调里避免闭包旧值；render 期同步更新） */
  const channelRef = useRef(channel);
  channelRef.current = channel;

  // 换账号：清掉渠道缓存并重拉，避免同一 tab 内新坐席沿用上一个坐席的当日渠道
  const userId = user?.id;
  useEffect(() => {
    setCachedEffectiveMode(null);
    setLoaded(false);
  }, [userId]);

  // 初值：当日 effective_mode（仅 chat_only / voice_only 会同时切换界面；历史 mixed 不改后端也不抢界面）
  useEffect(() => {
    if (!user?.id || !agent.loaded || loaded) return;
    getMyToday()
      .then((r) => {
        // 写入渠道缓存：工作台页面是懒加载子路由，可能读不到下面这一次性事件（见 api/shift 的缓存说明）。
        setCachedEffectiveMode(r.effective_mode);
        const { chat, voice } = modeToSwitches(r.effective_mode);
        if (chat && !voice) {
          setChannel('chat');
          window.dispatchEvent(new CustomEvent(WORKBENCH_SURFACE_EVENT, { detail: { surface: 'online' } }));
        } else if (voice && !chat) {
          setChannel('voice');
          window.dispatchEvent(new CustomEvent(WORKBENCH_SURFACE_EVENT, { detail: { surface: 'phone' } }));
        }
      })
      .catch(() => {
        /* 拦截器已提示；保持电话默认 */
      })
      .finally(() => setLoaded(true));
  }, [user?.id, agent.loaded, loaded]);

  /** 提交互斥渠道（chat_only / voice_only），成功后广播界面切换 */
  const switchTo = useCallback(
    async (next: Channel) => {
      if (busy) return;
      if (next === 'chat' && inCall) {
        Message.warning('通话中不能接在线会话，挂机后再切换');
        return;
      }
      if (next === channelRef.current) {
        // 渠道已是目标值不等于界面已一致（刷新后两边可能错开），同渠道也补发一次界面落位事件：
        // 幂等、不重复提交后端，保证「点一下」能把界面纠回渠道。别在这里改回静默早退。
        window.dispatchEvent(new CustomEvent(WORKBENCH_SURFACE_EVENT, { detail: { surface: surfaceOf(next) } }));
        return;
      }
      const prev = channelRef.current;
      const mode = next === 'chat' ? 'chat_only' : 'voice_only';
      setChannel(next);
      setBusy(true);
      try {
        await setMyChannelMode(mode);
        // 缓存同步：工作台页面下次挂载时按它落位（否则「切到电话→去别的页→回工作台」会被旧缓存拉回去）
        setCachedEffectiveMode(mode);
        window.dispatchEvent(new CustomEvent(CHANNEL_MODES_EVENT, { detail: { mode } }));
        window.dispatchEvent(new CustomEvent(WORKBENCH_SURFACE_EVENT, { detail: { surface: surfaceOf(next) } }));
        Message.success(
          next === 'chat' ? '已切换到在线会话：暂不接电话（可同时接多个会话）' : '已切换到电话：暂不接在线会话'
        );
      } catch {
        setChannel(prev);
      } finally {
        setBusy(false);
      }
    },
    [busy, inCall]
  );

  // 外部广播后同步展示：工作台界面被推送/手动切到另一边时，开关跟着落位（不提交后端）
  useEffect(() => {
    const onMode = (ev: Event) => {
      const mode = (ev as CustomEvent<{ mode: string }>).detail?.mode;
      if (!mode) return;
      const { chat, voice } = modeToSwitches(mode);
      if (chat && !voice) setChannel('chat');
      else if (voice && !chat) setChannel('voice');
    };
    const onSurface = (ev: Event) => {
      const surface = (ev as CustomEvent<{ surface?: WorkbenchSurface }>).detail?.surface;
      if (surface === 'online') setChannel('chat');
      else if (surface === 'phone') setChannel('voice');
    };
    window.addEventListener(CHANNEL_MODES_EVENT, onMode);
    window.addEventListener(WORKBENCH_SURFACE_EVENT, onSurface);
    return () => {
      window.removeEventListener(CHANNEL_MODES_EVENT, onMode);
      window.removeEventListener(WORKBENCH_SURFACE_EVENT, onSurface);
    };
  }, []);

  const chatActive = channel === 'chat';
  const voiceActive = channel === 'voice';
  const chatLocked = inCall;

  return (
    <Space size={8} align="center">
      <Tooltip
        content={chatLocked ? '通话中不能接在线会话：挂机后再切换' : '接在线会话（可同时接多个会话），暂不接电话'}
      >
        <span
          className={`bcc-header-capsule bcc-accept-item${chatActive ? ' on' : ' off'}${chatLocked ? ' is-locked' : ''}`}
          role="radio"
          aria-checked={chatActive}
          aria-disabled={chatLocked}
          tabIndex={0}
          onClick={() => void switchTo('chat')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void switchTo('chat');
            }
          }}
        >
          <span className="bcc-accept-label">会话</span>
        </span>
      </Tooltip>
      <Tooltip content="接电话，暂不接在线会话">
        <span
          className={`bcc-header-capsule bcc-accept-item${voiceActive ? ' on' : ' off'}`}
          role="radio"
          aria-checked={voiceActive}
          tabIndex={0}
          onClick={() => void switchTo('voice')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void switchTo('voice');
            }
          }}
        >
          <span className="bcc-accept-label">电话</span>
        </span>
      </Tooltip>
    </Space>
  );
}
